import { useDraggable } from '@dnd-kit/core';
import { formatInTimeZone } from 'date-fns-tz';
import { memo } from 'react';

import { Tooltip } from '@/ui';
import { cn } from '@/ui/lib/utils';

import { getEventColor } from '../../utils/eventColor';
import EventPopoverContent from '../EventPopoverContent';

import type { EventBlockProps } from './types';

function ResizeHandle({
  id,
  event,
  position
}: {
  id: string;
  event: EventBlockProps['event'];
  position: 'start' | 'end';
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id,
    data: { event, resizeEdge: position }
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={cn(
        'absolute inset-x-0 h-1.5 cursor-ns-resize',
        position === 'start' ? 'top-0' : 'bottom-0'
      )}
    />
  );
}

function EventBlock({
  event,
  timeZone,
  style,
  compact = false,
  draggable = true,
  onClick,
  renderEventContent,
  renderEventPopover
}: EventBlockProps) {
  const editable = event.editable ?? true;
  const resizable = event.resizable ?? editable;
  const canDrag = draggable && editable;
  const zone = event.timeZone ?? timeZone;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: event.id,
    data: { event },
    disabled: !canDrag
  });

  const color = getEventColor(event);
  const timeLabel = event.isAllDay
    ? undefined
    : `${formatInTimeZone(event.start, zone, 'h:mm a')} – ${formatInTimeZone(event.end, zone, 'h:mm a')}`;

  const content = renderEventContent ? (
    renderEventContent(event)
  ) : (
    <div className="flex flex-col overflow-hidden">
      <span className={cn('truncate font-medium', event.isCancelled && 'line-through')}>
        {event.title}
      </span>
      {!compact && timeLabel && (
        <span className="truncate text-[11px] opacity-90">{timeLabel}</span>
      )}
    </div>
  );

  const popoverContent = renderEventPopover ? (
    renderEventPopover(event)
  ) : (
    <EventPopoverContent event={event} timeZone={timeZone} />
  );

  if (event.isBackground) {
    return (
      <div
        ref={setNodeRef}
        data-testid={`calendar-event-background-${event.id}`}
        style={style}
        onClick={() => onClick?.(event)}
        className={cn(
          'absolute overflow-hidden rounded-md ring-1 ring-black/5',
          canDrag && 'cursor-grab active:cursor-grabbing',
          isDragging && 'opacity-30'
        )}
        {...(canDrag ? listeners : {})}
        {...(canDrag ? attributes : {})}>
        <div className="absolute inset-0 opacity-25" style={{ backgroundColor: color }} />
        <div className="relative flex h-full flex-col overflow-hidden px-1.5 py-0.5 text-[11px] font-medium text-uniform-gray-700 dark:text-uniform-gray-200">
          <span className="truncate">{event.title}</span>
          {timeLabel && <span className="truncate opacity-80">{timeLabel}</span>}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      data-testid={`calendar-event-${event.id}`}
      style={{ ...style, backgroundColor: color }}
      className={cn(
        'overflow-hidden rounded-md text-xs text-white shadow-sm',
        compact ? 'relative' : 'absolute',
        'ring-1 ring-black/10',
        event.isCancelled && 'border border-dashed border-white/70 opacity-70',
        canDrag && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-30'
      )}
      {...(canDrag ? listeners : {})}
      {...(canDrag ? attributes : {})}>
      <Tooltip content={popoverContent} interactive placement="right-start">
        <div
          onClick={() => onClick?.(event)}
          className={cn(
            'h-full w-full px-1.5 py-0.5',
            compact && 'flex items-center gap-1.5 truncate'
          )}>
          {compact && <span className="h-2 w-2 shrink-0 rounded-full bg-white/80" />}
          {content}
        </div>
      </Tooltip>
      {resizable && !compact && canDrag && (
        <>
          <ResizeHandle id={`${event.id}:resize-start`} event={event} position="start" />
          <ResizeHandle id={`${event.id}:resize-end`} event={event} position="end" />
        </>
      )}
    </div>
  );
}

export default memo(EventBlock);
